import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getEAProjects, getVaultDocuments } from '../services/ea-api'
import type { EAProjectStatus, EAProjectPhase } from '../types/ea'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
  boxShadow: '0 0 40px rgba(59, 130, 246, 0.05)',
}

const VENDOR_COLORS: Record<string, string> = {
  Arista: '#2563eb',
  PaloAlto: '#dc2626',
  Nutanix: '#059669',
  Azure: '#0284c7',
  Cisco: '#7c3aed',
}

const STATUS_COLORS: Record<string, string> = {
  active: '#10b981',
  on_hold: '#f59e0b',
  complete: '#3b82f6',
  cancelled: '#ef4444',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  on_hold: 'On Hold',
  complete: 'Complete',
  cancelled: 'Cancelled',
}

const PHASE_LABELS: Record<string, string> = {
  discovery: 'Discovery',
  design: 'Design',
  standards_gate: 'Standards Gate',
  deployment: 'Deployment',
  qc: 'QC Validation',
  deliverables: 'Deliverables',
  complete: 'Complete',
}

function StatusBadge({ status }: { status: EAProjectStatus }) {
  const color = STATUS_COLORS[status] || '#94a3b8'
  const label = STATUS_LABELS[status] || status
  return (
    <span
      className="px-2.5 py-1 text-xs font-medium rounded-full"
      style={{
        background: `${color}22`,
        color,
        border: `1px solid ${color}44`,
      }}
    >
      {label}
    </span>
  )
}

function PhaseBadge({ phase }: { phase: EAProjectPhase }) {
  const label = PHASE_LABELS[phase] || phase
  return (
    <span
      className="px-2.5 py-1 text-xs font-medium rounded-full"
      style={{
        background: 'rgba(139, 92, 246, 0.15)',
        color: '#a78bfa',
        border: '1px solid rgba(139, 92, 246, 0.3)',
      }}
    >
      {label}
    </span>
  )
}

function VendorBadge({ vendor }: { vendor: string }) {
  const color = VENDOR_COLORS[vendor] || '#94a3b8'
  return (
    <span
      className="px-2 py-0.5 text-xs font-medium rounded-full text-white"
      style={{ background: color }}
    >
      {vendor}
    </span>
  )
}

export default function EADashboard() {
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['ea-projects'],
    queryFn: () => getEAProjects().then((res) => res.data),
  })

  const { data: vaultData } = useQuery({
    queryKey: ['ea-vault-docs'],
    queryFn: () => getVaultDocuments().then((res) => res.data),
  })

  const projects = Array.isArray(projectsData) ? projectsData : []
  const vaultDocs = Array.isArray(vaultData) ? vaultData : []

  const stats = {
    activeProjects: projects.filter((p) => p.status === 'active').length,
    runningTasks: 0,
    vaultDocuments: vaultDocs.length,
    qcFindings: 0,
  }

  if (projectsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)',
            }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading EA-Orchestrator...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div
        className="rounded-2xl p-8 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.15) 50%, rgba(6, 182, 212, 0.1) 100%)',
          border: '1px solid rgba(100, 180, 255, 0.2)',
          boxShadow: '0 0 60px rgba(59, 130, 246, 0.1)',
        }}
      >
        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full -mr-48 -mt-48"
          style={{ background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)' }}
        ></div>
        <div
          className="absolute bottom-0 left-0 w-64 h-64 rounded-full -ml-32 -mb-32"
          style={{ background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)' }}
        ></div>
        <div className="relative z-10">
          <h1
            className="text-3xl font-bold mb-2"
            style={{
              background: 'linear-gradient(135deg, #e2e8f0 0%, #94a3b8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            EA-Orchestrator
          </h1>
          <p style={{ color: 'rgba(148, 163, 184, 0.9)' }} className="text-lg">
            Enterprise Architecture Workflow Engine
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div
          className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]"
          style={{ ...glassPanel, borderColor: 'rgba(16, 185, 129, 0.2)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)' }}
            >
              <i className="fa-solid fa-diagram-project text-xl" style={{ color: '#10b981' }}></i>
            </div>
            <span
              className="text-xs font-medium px-3 py-1 rounded-full"
              style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)' }}
            >
              Active
            </span>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Active Projects</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.activeProjects}</p>
        </div>

        <div
          className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]"
          style={{ ...glassPanel, borderColor: 'rgba(59, 130, 246, 0.2)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)' }}
            >
              <i className="fa-solid fa-gears text-xl" style={{ color: '#60a5fa' }}></i>
            </div>
            <span
              className="text-xs font-medium px-3 py-1 rounded-full"
              style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)' }}
            >
              Tasks
            </span>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Running Tasks</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.runningTasks}</p>
        </div>

        <div
          className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]"
          style={{ ...glassPanel, borderColor: 'rgba(139, 92, 246, 0.2)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(139, 92, 246, 0.15)', border: '1px solid rgba(139, 92, 246, 0.3)' }}
            >
              <i className="fa-solid fa-vault text-xl" style={{ color: '#a78bfa' }}></i>
            </div>
            <span
              className="text-xs font-medium px-3 py-1 rounded-full"
              style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)' }}
            >
              Vault
            </span>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Vault Documents</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.vaultDocuments}</p>
        </div>

        <div
          className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]"
          style={{ ...glassPanel, borderColor: 'rgba(251, 191, 36, 0.2)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(251, 191, 36, 0.15)', border: '1px solid rgba(251, 191, 36, 0.3)' }}
            >
              <i className="fa-solid fa-clipboard-check text-xl" style={{ color: '#fbbf24' }}></i>
            </div>
            <span
              className="text-xs font-medium px-3 py-1 rounded-full"
              style={{ background: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' }}
            >
              QC
            </span>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>QC Findings</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.qcFindings}</p>
        </div>
      </div>

      {/* Recent Projects */}
      <div className="rounded-xl p-6" style={glassPanel}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold" style={{ color: '#e2e8f0' }}>Recent Projects</h2>
          <Link
            to="/ea/projects"
            className="text-sm font-medium flex items-center transition-all duration-200 hover:gap-3 gap-2"
            style={{ color: '#60a5fa' }}
          >
            View all
            <i className="fa-solid fa-arrow-right"></i>
          </Link>
        </div>

        {projects.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider pb-3 px-3" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>Project</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider pb-3 px-3" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>Client</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider pb-3 px-3" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>Vendors</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider pb-3 px-3" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>Status</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider pb-3 px-3" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>Phase</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider pb-3 px-3" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {projects.slice(0, 8).map((project) => (
                  <tr
                    key={project.id}
                    className="transition-colors duration-200 cursor-pointer group"
                    style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.05)' }}
                  >
                    <td className="py-3 px-3">
                      <Link to={`/ea/projects/${project.id}`} className="block">
                        <p className="text-sm font-semibold group-hover:text-blue-400 transition-colors" style={{ color: '#e2e8f0' }}>
                          {project.project_name}
                        </p>
                        <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>{project.project_code}</p>
                      </Link>
                    </td>
                    <td className="py-3 px-3">
                      <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>{project.client_name}</span>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex flex-wrap gap-1">
                        {project.vendors.map((v) => (
                          <VendorBadge key={v} vendor={v} />
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <StatusBadge status={project.status} />
                    </td>
                    <td className="py-3 px-3">
                      <PhaseBadge phase={project.current_phase} />
                    </td>
                    <td className="py-3 px-3">
                      <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                        {new Date(project.updated_at).toLocaleDateString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <div
              className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
            >
              <i className="fa-solid fa-diagram-project text-3xl" style={{ color: 'rgba(96, 165, 250, 0.5)' }}></i>
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>No EA projects yet</h3>
            <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Create your first enterprise architecture project to get started</p>
            <Link
              to="/ea/projects/new"
              className="px-6 py-3 rounded-xl text-sm font-medium inline-flex items-center space-x-2 transition-all duration-200 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
                border: '1px solid rgba(96, 165, 250, 0.4)',
                color: '#e2e8f0',
              }}
            >
              <i className="fa-solid fa-plus"></i>
              <span>New EA Project</span>
            </Link>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="rounded-xl p-6" style={glassPanel}>
        <h2 className="text-lg font-bold mb-6" style={{ color: '#e2e8f0' }}>Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/ea/projects/new"
            className="p-6 rounded-xl transition-all duration-300 hover:scale-[1.02] group"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
            }}
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
              style={{ background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)' }}
            >
              <i className="fa-solid fa-plus text-2xl" style={{ color: '#60a5fa' }}></i>
            </div>
            <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>New EA Project</h3>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              Start a new enterprise architecture engagement
            </p>
          </Link>

          <Link
            to="/ea/projects"
            className="p-6 rounded-xl transition-all duration-300 hover:scale-[1.02] group"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(52, 211, 153, 0.2)',
            }}
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
              style={{ background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)' }}
            >
              <i className="fa-solid fa-list-check text-2xl" style={{ color: '#34d399' }}></i>
            </div>
            <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>View All Projects</h3>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              Browse and manage all EA projects
            </p>
          </Link>

          <div
            className="p-6 rounded-xl"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
            }}
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
              style={{ background: 'rgba(139, 92, 246, 0.15)', border: '1px solid rgba(139, 92, 246, 0.3)' }}
            >
              <i className="fa-solid fa-vault text-2xl" style={{ color: '#a78bfa' }}></i>
            </div>
            <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>Vendor Vault</h3>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              Browse vendor documentation and best practices
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
